<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <h1 class="h2 mb-1 fw-bold">Service Appointments</h1>
    <p class="text-muted mb-0">Request and track drone service appointments</p>
  </div>
  <button class="btn btn-primary" (click)="requestAppointment()">
    <i class="bi bi-plus-lg me-2"></i>Request Appointment
  </button>
</div>

<!-- Stats Summary -->
<div class="row g-3 mb-4">
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-primary-subtle">
            <i class="bi bi-clock text-primary"></i>
          </div>
          <div>
            <div class="stat-value">{{ requestedCount }}</div>
            <div class="stat-label text-muted">Requested</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-info-subtle">
            <i class="bi bi-calendar-check text-info"></i>
          </div>
          <div>
            <div class="stat-value">{{ scheduledConfirmedCount }}</div>
            <div class="stat-label text-muted">Scheduled / Confirmed</div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="col-md-4">
    <div class="card stat-card">
      <div class="card-body">
        <div class="d-flex align-items-center gap-3">
          <div class="stat-icon bg-success-subtle">
            <i class="bi bi-check-circle text-success"></i>
          </div>
          <div>
            <div class="stat-value">{{ completedCount }}</div>
            <div class="stat-label text-muted">Completed</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Loading State -->
<div *ngIf="isLoading" class="d-flex justify-content-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<!-- Appointments List -->
<div class="card" *ngIf="!isLoading">
  <!-- Card Header with Status Filter -->
  <div class="card-header p-3">
    <div class="d-flex justify-content-between align-items-center">
      <h5 class="mb-0">Your Appointments</h5>
      <div class="d-flex gap-2 align-items-center">
        <select
          class="form-select form-select-sm w-180px"
          [(ngModel)]="filters.status"
          (ngModelChange)="applyFilters()"
        >
          <option value="">All Statuses</option>
          <option *ngFor="let status of statusOptions" [value]="status.value">{{ status.label }}</option>
        </select>
        <button
          *ngIf="activeFilterCount > 0"
          class="btn btn-sm btn-link text-decoration-none text-muted"
          (click)="clearFilters()"
        >
          <i class="bi bi-x-lg me-1"></i>Clear
        </button>
      </div>
    </div>
  </div>

  <div class="card-body p-0">
    <ng-container *ngIf="!isLoading && appointments.length > 0; else emptyState">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead>
            <tr>
              <th class="ps-3">Preferred Date</th>
              <th>Drone</th>
              <th>Status</th>
              <th>Technician</th>
              <th>Issue</th>
              <th class="text-end pe-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let appointment of appointments" class="record-row cursor-pointer" (click)="viewAppointment(appointment)">
              <td class="ps-3">
                <span class="fw-medium">{{ formatDate(appointment.preferredDate) }}</span>
              </td>
              <td>
                <span class="fw-medium">{{ appointment.droneName || 'Unknown' }}</span>
              </td>
              <td>
                <span class="badge" [ngClass]="getStatusBadgeClass(appointment.status)">
                  {{ getStatusText(appointment.status) }}
                </span>
              </td>
              <td>
                <span class="text-muted">{{ appointment.technicianName || 'Pending' }}</span>
              </td>
              <td>
                <span class="text-muted">{{ truncateText(appointment.issueDescription) }}</span>
              </td>
              <td class="text-end pe-3" (click)="$event.stopPropagation()">
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-secondary" (click)="viewAppointment(appointment)" title="View details">
                    <i class="bi bi-eye"></i>
                  </button>
                  <button
                    class="btn btn-outline-secondary text-danger"
                    (click)="openCancelModal(appointment)"
                    title="Cancel appointment"
                    *ngIf="isCancellable(appointment.status)"
                  >
                    <i class="bi bi-x-circle"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer d-flex justify-content-between align-items-center p-3" *ngIf="totalPages > 1">
        <small class="text-muted">
          Page {{ currentPage }} of {{ totalPages }}
        </small>
        <nav>
          <ul class="pagination pagination-sm mb-0">
            <li class="page-item" [class.disabled]="currentPage === 1">
              <a class="page-link" (click)="prevPage()">Previous</a>
            </li>
            <li class="page-item" [class.disabled]="currentPage === totalPages">
              <a class="page-link" (click)="nextPage()">Next</a>
            </li>
          </ul>
        </nav>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="p-5 text-center">
        <div class="empty-state-icon mb-3">
          <i class="bi bi-calendar-check"></i>
        </div>
        <h5>No Service Appointments Yet</h5>
        <p class="text-muted mb-3">Request a service appointment when your drone needs maintenance.</p>
        <button class="btn btn-primary" (click)="requestAppointment()">
          <i class="bi bi-plus-lg me-2"></i>Request First Appointment
        </button>
      </div>
    </ng-template>
  </div>
</div>

<!-- Cancel Confirmation Modal -->
<div class="modal fade" [class.show]="showCancelModal" [style.display]="showCancelModal ? 'block' : 'none'" tabindex="-1" role="dialog">
  <div class="modal-dialog modal-dialog-centered" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Cancel Appointment</h5>
        <button type="button" class="btn-close" (click)="closeCancelModal()" [disabled]="isCancelling"></button>
      </div>
      <div class="modal-body">
        <p class="text-muted">Are you sure you want to cancel this appointment? This action cannot be undone.</p>
        <div class="mb-3">
          <label for="cancelReason" class="form-label">Reason for cancellation <span class="text-danger">*</span></label>
          <textarea appCharCounter
            id="cancelReason"
            class="form-control"
            rows="3"
            [(ngModel)]="cancelReason"
            placeholder="Please provide a reason for cancellation..."
            [disabled]="isCancelling"
          ></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="closeCancelModal()" [disabled]="isCancelling">
          Nevermind
        </button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="confirmCancel()"
          [disabled]="!cancelReason.trim() || isCancelling"
        >
          <span *ngIf="isCancelling" class="spinner-border spinner-border-sm me-2"></span>
          Confirm Cancellation
        </button>
      </div>
    </div>
  </div>
</div>
<div class="modal-backdrop fade" [class.show]="showCancelModal" *ngIf="showCancelModal"></div>
